import { Button } from '@altify/components';
import { useContent } from '@altify/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useProduct } from 'hooks/products/useProducts';
import useCountry from 'hooks/useCountry';
import {
  useGetPersonalDataQuery,
  useGetVaspsQuery,
} from 'store/slices/crypto-accounts-api';
import { z } from 'zod';
import CryptoWithdrawalWrapper from '../../../components/CryptoWithdrawalWrapper';
import CryptoAccountConfirmView from '../../components/CryptoAccountConfirmView';
import { content } from './route.content';
const searchParamsSchema = z.object({
  accountId: z.string().optional(),
  address: z.string().optional(),
  isHosted: z.boolean().optional(),
  institutionId: z.string().optional(),
  name: z.string().optional(),
});
export const Route = createFileRoute(
  '/_authenticated/funds/withdraw/crypto/$productCode/wallet/edit/confirm',
)({
  component: WithdrawCryptoConfirm,
  validateSearch: zodValidator(searchParamsSchema),
});

function WithdrawCryptoConfirm() {
  const localizedContent = useContent(content);
  const navigate = useNavigate();
  const { productCode } = Route.useParams();

  const { product } = useProduct(productCode);
  const payload: {
    accountId: string;
    address: string;
    isHosted: boolean;
    institutionId?: string;
    name?: string;
  } = Route.useSearch();

  const { data: personalData, isLoading: personalDataIsLoading } =
    useGetPersonalDataQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });
  const { data: vaspsData, isLoading: isVaspsLoading } = useGetVaspsQuery();
  const isLoading = personalDataIsLoading || isVaspsLoading;
  const countries = useCountry();
  const residenceCountry = countries.find(
    c => c.code === personalData?.residencyCountryCode,
  )?.name;
  const personalAddressLabel = personalData
    ? `${personalData.streetName} ${personalData.streetNumber}, ${personalData.residencyCity}, ${personalData.zipCode}, ${residenceCountry}`
    : undefined;
  const institutionName =
    vaspsData?.find(v => v.id === payload.institutionId)?.name ?? payload.name;

  const handleConfirmDetails = async () => {
    navigate({
      to: '/funds/withdraw/crypto/$productCode/wallet/edit/otp',
      params: { productCode: productCode },
      search: payload,
    });
  };

  const bodyContent = (
    <>
      {isLoading ? (
        <div className="w-full h-1/3 rounded-lg skeleton" />
      ) : (
        <CryptoAccountConfirmView
          productName={product?.name!}
          address={payload.address}
          personalAddressLabel={personalAddressLabel}
          isHosted={payload.isHosted}
          institutionName={institutionName}
        />
      )}
    </>
  );

  const buttons = (
    <div className="flex flex-col items-center flex-grow justify-end w-full gap-4">
      <Button
        disabled={isLoading}
        color="secondary"
        size="large"
        text={localizedContent.edit}
        className="w-full"
        onPress={() =>
          navigate({
            to: `/funds/withdraw/crypto/${productCode}/wallet/edit/travel-rule`,
          })
        }
      />
      <Button
        disabled={isLoading}
        color="primary"
        size="large"
        className="w-full"
        text={localizedContent.confirm}
        onPress={handleConfirmDetails}
      />
    </div>
  );
  return (
    <CryptoWithdrawalWrapper
      isLoading={isLoading}
      title={localizedContent.title(product?.name!)}
      bodyContent={bodyContent}
      buttons={buttons}
    />
  );
}
